import React, { type HTMLAttributes } from 'react';
import { cn } from '@/app/_lib/utils';

export type ListVariant = 'default' | 'styled';

export interface ListProps extends HTMLAttributes<HTMLUListElement | HTMLOListElement> {
  children: React.ReactNode;
  className?: string;
  /** Render as ordered list (ol) instead of unordered (ul) */
  ordered?: boolean;
  /** Visual style variant */
  variant?: ListVariant;
}

/**
 * List component for ul/ol lists.
 * Based on Figma specs: pl-6, text-base, font-medium
 */
export function List({ children, className, ordered = false, variant = 'default', ...props }: ListProps) {
  const Tag = ordered ? 'ol' : 'ul';
  const isStyled = variant === 'styled';
  const listStyle = isStyled
    ? 'list-none'
    : ordered
      ? 'list-decimal'
      : 'list-[square]';

  return (
    <Tag
      className={cn(
        isStyled
          ? 'w-full my-6 flex flex-wrap gap-3 text-base font-medium text-inherit text-left pl-0'
          : 'w-fit pl-8 my-6 space-y-1 text-base font-medium text-inherit text-left list-outside',
        listStyle,
        isStyled && '[&>li]:rounded-lg [&>li]:bg-muted [&>li]:px-4 [&>li]:py-2.5 [&>li]:transition-colors [&>li:has(a)]:cursor-pointer [&>li:has(a):hover]:bg-muted/70 [&_a]:text-inherit [&_a]:no-underline',
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}
